import "../css/subpage.less";
import PageMeta from "../common/PageMeta";
import { PAGE_META } from "../seo/pageMeta";
import React, { useEffect, useState } from "react";
import { getImageUrl } from "../config";
import { useTranslation } from "react-i18next";
import { fetchLocalizedJson } from "../api/fetchLocalizedJson";
import { useLocation } from "react-router-dom";

import * as CustomerEntity from "../entities/Customer";
import SkeletonView from "../common/SkeletonView";
import SubpageTitle from "../common/SubpageTitle";
import FaqStickyLink from "../common/FaqStickyLink";

const Company: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        const yOffset = -90;
        const y =
          element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [hash]);

  const [customers, setEvents] = useState<CustomerEntity.Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchLocalizedJson<CustomerEntity.Customer[]>("customer.json")
      .then((data) => {
        setEvents(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError(error.message);
        setLoading(false);
      });
  }, [i18n.language]);

  const companyHistory = [
    { date: "2025/04", text: t("company_page.h_2025_4_1") },
    { date: "2025/04", text: t("company_page.h_2025_4_2") },
    { date: "2025/02", text: t("company_page.h_2025_2") },
    { date: "2024/12", text: t("company_page.h_2024_12") },
    { date: "2024/05", text: t("company_page.h_2024_5") },
    { date: "2023/01", text: t("company_page.h_2023_1") },
    { date: "2020/03", text: t("company_page.h_2020_3") },
    { date: "2020/01", text: t("company_page.h_2020_1") },
  ];

  if (loading) {
    return <SkeletonView />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const companyOverviewRows: { key: string; label: string; content: React.ReactNode }[] =
    [
      { key: "1", label: t("company_page.label_name"), content: t("footer.company_name") },
      {
        key: "2",
        label: t("company_page.label_location"),
        content: (
          <>
            {t("company_page.headquarters")}
            <br />
            {t("company_page.branch")}
          </>
        ),
      },
      { key: "3", label: t("company_page.label_founded"), content: t("company_page.val_founded") },
      { key: "5", label: t("company_page.label_capital"), content: t("company_page.val_capital") },
      { key: "6", label: t("company_page.label_ceo"), content: t("company_page.president_name") },
      { key: "7", label: t("company_page.label_tdb"), content: "533054707" },
      {
        key: "8",
        label: t("company_page.label_orgs"),
        content: (
          <>
            横浜商工会議所
            <br />
            川崎商工会議所
          </>
        ),
      },
      {
        key: "9",
        label: t("company_page.label_licenses"),
        content: (
          <>
            {t("company_page.license_dispatch_prefix")}(
            <a
              href="https://jinzai.hellowork.mhlw.go.jp/JinzaiWeb/GICB102010.do?screenId=GICB102010&action=detail&detkey_Detail=%E6%B4%BE14-303679%2C1+++++"
              target="_blank"
              rel="noopener noreferrer"
            >
              派14-303679
            </a>
            )
            <br />
            {t("company_page.license_pmark_prefix")}(
            <a
              href="https://entity-search.jipdec.or.jp/pmark/details?tdfkRadios=&kanaRadios=&eda_number=&company_address=&industry_type1=%E6%8C%87%E5%AE%9A%E3%81%AA%E3%81%97&industry_type2=%E6%8C%87%E5%AE%9A%E3%81%AA%E3%81%97&examining_authority=%E6%8C%87%E5%AE%9A%E3%81%AA%E3%81%97&login_number=10825404"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("company_page.license_pmark_num")}
            </a>
            )
          </>
        ),
      },

      {
        key: "10",
        label: t("company_page.label_banks"),
        content: (
          <>
            みずほ銀行 / 横浜銀行 / 横浜信用金庫 / 城南信用金庫
          </>
        ),
      },
    ];

  return (
    <>
      <PageMeta path="/company" {...PAGE_META["/company"]} />
      <div className="subpage subpage--company">

        <SubpageTitle titleJa="会社情報" titleEn="Company" />
        <FaqStickyLink />
        <section className="company-section company-section--message" aria-labelledby="t1">
          <div className="message-main">
            <div className="message-content">
              <h3 id="t1" className="message-title">
                {t("company_page.president_title")}
              </h3>
              <p
                className="message-content-text"
                style={{ marginTop: "0", fontSize: "13px" }}
              >
                {t("company_page.president_body_1")}
                <br />
                <br />
                {t("company_page.president_body_2")}
                <br />
                <br />
                {t("company_page.president_body_3")}
                <br />
                <br />
                {t("company_page.president_body_4")}
                <br />
                <br />
                {t("company_page.president_body_5")}
              </p>
              <div style={{ width: "100%", textAlign: "right" }}>
                <p style={{ fontSize: "12px", marginBottom: "10px" }}>
                  {t("company_page.president_role")}
                </p>
                <p style={{ fontSize: "20px", margin: "0px" }}>{t("company_page.president_name")}</p>
              </div>
            </div>
            <div className="message-imgdiv">
              <img
                src={getImageUrl("/image/president.jpg")}
                className="message-image"
                alt={t("company_page.president_role")}
              />
            </div>
          </div>
        </section>

        <section className="company-section" aria-labelledby="t3">
          <h2 id="t3" className="company-title">
            {t("company_page.philosophy_title")}
          </h2>
          <div className="company-about">
            <p>{t("company_page.philosophy_1")}</p>
            <p>{t("company_page.philosophy_2")}</p>
            <p>{t("company_page.philosophy_3")}</p>
            <p>{t("company_page.philosophy_4")}</p>
          </div>
        </section>

        <section className="company-section" aria-labelledby="t2">
          <h2 id="t2" className="company-title">
            {t("company_page.overview_title")}
          </h2>
          <dl className="company-overview">
            {companyOverviewRows.map((row) => (
              <div key={row.key} className="company-overview__row">
                <dt className="company-overview__label">{row.label}</dt>
                <dd className="company-overview__value">{row.content}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section
          className="company-section company-partners"
          aria-labelledby="t4"
        >
          <h2 id="t4" className="company-title">
            {t("company_page.partners_title")}
          </h2>
          <div className="company-partners__logos">
            {customers.map((customer) => (
              <a
                key={customer.title}
                className="company-partners__link"
                href={customer.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getImageUrl(customer.image)}
                  alt={customer.title}
                  title={customer.title}
                  className="company-partners__logo"
                />
              </a>
            ))}
          </div>
          <p className="company-partners__note">
            {t("company_page.partners_note")}
          </p>
        </section>

        <section className="company-section company-section--map" aria-labelledby="t5">
          <h2 id="t5" className="company-title">
            {t("company_page.map_title")}
          </h2>
          <div className="company-map-wrap">
            <iframe
              title="wessoft-map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3247.3191721591684!2d139.6912718762257!3d35.52110953875269!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601860a6530810bf%3A0xbee07705e831d08a!2z44CSMjEwLTAwMjIg56 神奈川県川崎市川崎区池田2丁目6番7号!5e0!3m2!1sja!2sjp!4v1733962646894!5m2!1sja!2sjp"
              className="google-map"
              loading="lazy"
            />
          </div>
        </section>

        <section className="company-section company-history" aria-labelledby="t6">
          <h2 id="t6" className="company-title">
            {t("company_page.history_title")}
          </h2>
          <ul className="company-history__list">
            {companyHistory.map((row) => (
              <li
                key={`${row.date}-${row.text}`}
                className="company-history__item"
              >
                <div className="company-history__item-content">
                  <span className="company-history__date">{row.date}</span>
                  <span className="company-history__text">{row.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
};

export default Company;
